/**
 * @file FormulationExportModal.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-06
 * @modified 2026-10-02
 * @description Export one or more formulation sheets as a branded PDF —
 *              download, print on the office printer via the Pi bridge, or
 *              email to yourself or someone else.
 *
 *              All three modes hit POST /api/formulations/export; only
 *              `download` comes back as a PDF body, the others return JSON.
 *
 *              The mode is picked in the footer (ExportModeFooter), beside the
 *              button that acts on it, as in the blend export.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import ExportPreview from './ExportPreview';
import ExportLayoutOptions from './ExportLayoutOptions';
import ExportModeFooter, { EXPORT_MODES, revealOnMount } from './ExportModeFooter';
import './Formulations.css';

// Per-sheet product name. A formulation sheet often goes to a mill or
// supplier, and a bespoke blend is named after the farm it's made for — which
// that supplier has no business knowing. Three choices per product, because no
// automatic rule catches a farm referred to by a name that isn't on its
// account: the name as stored, the auto-stripped suggestion, or the user's own.
function NameChoice({ sheet, choice, custom, onChoose, onCustom }) {
  const stripAvailable = sheet.stripped_name && sheet.stripped_name !== sheet.item_name;
  const customers = (sheet.customers || []).map(c => c.account_name).filter(Boolean);

  return (
    <li className="fm-name-choice">
      <div className="fm-name-choice-head">
        <span className="account-badge-inline">{sheet.item_code}</span>
        {customers.length > 0 && (
          <span className="fm-name-choice-customer" title={customers.join(', ')}>
            {customers.length === 1 ? customers[0] : `${customers.length} customers`}
          </span>
        )}
      </div>

      <div className="fm-name-choice-options">
        <label className={choice === 'original' ? 'active' : undefined}>
          <input
            type="radio"
            name={`fm-name-${sheet.id}`}
            checked={choice === 'original'}
            onChange={() => onChoose('original')}
          />
          <span className="fm-name-option-label">As stored</span>
          <span className="fm-name-option-value">{sheet.item_name}</span>
        </label>

        {/* Rendered disabled rather than hidden when nothing was found to
            strip, so every product shows the same three rows and the list
            doesn't go ragged. */}
        <label className={`${choice === 'stripped' ? 'active' : ''}${stripAvailable ? '' : ' disabled'}`}>
          <input
            type="radio"
            name={`fm-name-${sheet.id}`}
            checked={choice === 'stripped'}
            disabled={!stripAvailable}
            onChange={() => onChoose('stripped')}
          />
          <span className="fm-name-option-label">Customer removed</span>
          <span className="fm-name-option-value">
            {stripAvailable ? sheet.stripped_name : 'No customer name found in this product name'}
          </span>
        </label>

        <label className={choice === 'custom' ? 'active' : undefined}>
          <input
            type="radio"
            name={`fm-name-${sheet.id}`}
            checked={choice === 'custom'}
            onChange={() => onChoose('custom')}
          />
          <span className="fm-name-option-label">Custom</span>
          <Input
            type="text"
            value={custom}
            onChange={(e) => { onCustom(e.target.value); onChoose('custom'); }}
            onFocus={() => onChoose('custom')}
            placeholder="Type the name to print…"
            aria-label={`Custom printed name for ${sheet.item_code}`}
          />
        </label>
      </div>
    </li>
  );
}

function ExportModalBody({
  mode, count, period, to, setTo, subject, setSubject, message, setMessage, error,
  sheets, choices, customs, setChoice, setCustom, layout, setLayoutField, previewPayload
}) {
  return (
    <div className="formulation-export-split">
    <div className="formulation-export-body">
      {error && <div className="formulation-error">{error}</div>}

      <p className="fm-export-summary">
        {count === 1 ? '1 formulation sheet' : `${count} formulation sheets`} for <strong>{period}</strong>
        {count > 1 && ' — one product per page'}
      </p>

      {sheets.length > 0 && (
        <div className="fm-export-names">
          <div className="fm-export-names-head">
            <span className="fm-export-names-title">Product name on the sheet</span>
            <span className="fm-export-names-hint">
              Sheets sent to a supplier shouldn't name the customer
            </span>
          </div>
          <ul className="fm-name-choice-list">
            {sheets.map((s) => (
              <NameChoice
                key={s.id}
                sheet={s}
                choice={choices[s.id] || 'original'}
                custom={customs[s.id] ?? s.item_name}
                onChoose={(c) => setChoice(s.id, c)}
                onCustom={(v) => setCustom(s.id, v)}
              />
            ))}
          </ul>
        </div>
      )}

      <ExportLayoutOptions
        orientation={layout.orientation}
        onOrientation={(v) => setLayoutField('orientation', v)}
        fit={layout.fit}
        onFit={(v) => setLayoutField('fit', v)}
        scale={layout.scale}
        onScale={(v) => setLayoutField('scale', v)}
      />

      {/* The mode is picked in the footer; the fields Email needs stay here,
          where there is room for them to scroll. */}
      {mode === 'email' && (
        <div className="fm-export-email" ref={revealOnMount}>
          <div className="fm-export-field">
            <label htmlFor="fm-export-to">To</label>
            <Input
              id="fm-export-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="Leave blank to send to yourself"
              icon="mail"
            />
          </div>
          <div className="fm-export-field">
            <label htmlFor="fm-export-subject">Subject</label>
            <Input
              id="fm-export-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Default subject will be used if blank"
            />
          </div>
          <div className="fm-export-field">
            <label htmlFor="fm-export-message">Message</label>
            <Textarea
              id="fm-export-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional covering message…"
              rows={3}
            />
          </div>
        </div>
      )}
    </div>

    {/* The sheet itself, rendered from the same html the PDF is built from —
        see ExportPreview. Capped at five sheets server-side; the preview is
        there to check the layout, not to read the whole batch. */}
    <ExportPreview
      endpoint="/api/formulations/export/preview"
      payload={previewPayload}
      note={count > 5 ? `First 5 of ${count} sheets` : ''}
    />
    </div>
  );
}

// No .footer-right: that sets the footer to space-between, which would leave
// the button row on the left once it wraps onto a row of its own.
function ExportModalFooter({ mode, setMode, busy, onClose, onSubmit }) {
  const verb = mode === 'download' ? 'Download' : mode === 'print' ? 'Print' : 'Send';
  const icon = mode === 'download' ? 'download' : mode === 'print' ? 'print' : 'send';

  return (
    <ExportModeFooter modes={EXPORT_MODES} mode={mode} onMode={setMode} busy={busy}>
      <div>
        <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button className="btn-slideout" onClick={onSubmit} disabled={busy}>
          <div className="icon-wrapper">
            <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>
              {busy ? 'progress_activity' : icon}
            </span>
          </div>
          <span className="btn-label">{busy ? 'Working…' : verb}</span>
        </button>
      </div>
    </ExportModeFooter>
  );
}

function FormulationExportModal({ ids, period, accountNames, onClose, onDone }) {
  const [mode, setMode] = useState('download');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sheets, setSheets] = useState([]);
  const [choices, setChoices] = useState({});
  const [customs, setCustoms] = useState({});
  // A formulation sheet is one product per page rather than a wide table, so
  // it opens portrait — unlike the table exports.
  const [layout, setLayout] = useState({ orientation: 'portrait', fit: 'width', scale: 1 });
  const setLayoutField = useCallback(
    (key, value) => setLayout((prev) => ({ ...prev, [key]: value })), []);

  // Names and their stripped suggestions come from the server, which is where
  // the customer links live. Failing to load them is not fatal — the export
  // still works, it just prints the stored names.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/formulations/export/names?ids=${ids.join(',')}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d?.sheets) return;
        setSheets(d.sheets);
        // Default to the stored name. Stripping is a deliberate act — silently
        // renaming a product on a sheet the user thought they were printing
        // as-is would be worse than making them tick a box.
        setCustoms(Object.fromEntries(d.sheets.map((s) => [s.id, s.item_name])));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ids]);

  const setChoice = (id, choice) => setChoices((prev) => ({ ...prev, [id]: choice }));
  const setCustom = (id, value) => setCustoms((prev) => ({ ...prev, [id]: value }));

  // Only names that actually differ from what's stored are sent — the server
  // leaves the rest alone, so an untouched export behaves exactly as before.
  const buildNameOverrides = () => {
    const out = {};
    for (const s of sheets) {
      const choice = choices[s.id] || 'original';
      const name = choice === 'stripped' ? s.stripped_name
        : choice === 'custom' ? (customs[s.id] ?? '').trim()
        : null;
      if (name && name !== s.item_name) out[s.id] = name;
    }
    return out;
  };

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/formulations/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ids, period, mode, to, subject, message, accountNames,
          nameOverrides: buildNameOverrides(),
          layout
        })
      });

      if (mode === 'download') {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to generate PDF');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = ids.length === 1 ? 'formulation.pdf' : `formulations-${ids.length}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Revoke on the next tick — revoking synchronously can cancel the
        // download in some browsers before it has started reading the blob.
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        onDone(`Downloaded ${ids.length === 1 ? 'formulation sheet' : `${ids.length} formulation sheets`}`);
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Export failed');
      onDone(data.message || 'Done');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const previewPayload = useMemo(
    () => ({ ids, period, accountNames, nameOverrides: buildNameOverrides(), layout }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids, period, accountNames, layout, sheets, choices, customs]);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={ids.length === 1 ? 'Export Formulation' : `Export ${ids.length} Formulations`}
      // The preview sits beside the choices, which needs the width the table
      // and blend exports already take.
      size="lg"
      className="formulation-export"
      footer={
        <ExportModalFooter mode={mode} setMode={setMode} busy={busy}
                           onClose={onClose} onSubmit={submit} />
      }
    >
      <ExportModalBody
        mode={mode}
        count={ids.length}
        period={period}
        to={to}
        setTo={setTo}
        subject={subject}
        setSubject={setSubject}
        message={message}
        setMessage={setMessage}
        error={error}
        sheets={sheets}
        choices={choices}
        customs={customs}
        setChoice={setChoice}
        setCustom={setCustom}
        layout={layout}
        setLayoutField={setLayoutField}
        previewPayload={previewPayload}
      />
    </Modal>
  );
}

export default FormulationExportModal;
